<template>
  <div class="overview-networks ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">{{ byGroup ? 'folder_open' : 'public' }}</i>
      <Tooltip :text="$t(byGroup ? 'dash.overview.help.groups' : 'dash.overview.help.networks')" position="top">
        <h2 class="ov-help-title">{{ $t(byGroup ? 'dash.overview.groups.title' : 'dash.overview.networks.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <div class="ov-presets nw-switch">
        <button class="ov-preset" :class="{ active: !byGroup }" @click="setMode('network')">{{ $t('dash.overview.groups.by_network') }}</button>
        <button class="ov-preset" :class="{ active: byGroup }" @click="setMode('group')">{{ $t('dash.overview.groups.by_page') }}</button>
      </div>
    </div>

    <div v-if="loading" class="nw-skeleton"></div>
    <p v-else-if="!rows.length" class="ov-empty">{{ $t('dash.overview.networks.empty') }}</p>
    <div v-else class="nw-table-wrap">
      <table class="nw-table">
        <thead>
          <tr>
            <th>{{ $t(byGroup ? 'dash.overview.groups.group' : 'dash.overview.networks.network') }}</th>
            <th v-for="col in columns" :key="col.key">
              <Tooltip :text="col.help" position="top"><span>{{ col.label }}</span></Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows">
          <tr
            :key="row.key"
            class="nw-row"
            :class="{ 'nw-row--open': openKey === row.key, 'nw-row--expandable': row.details.length }"
            @click="toggle(row)"
          >
            <td class="nw-name">
              <i v-if="row.details.length" class="material-icons nw-caret">{{ openKey === row.key ? 'expand_less' : 'expand_more' }}</i>
              {{ row.label }}
            </td>
            <td>{{ row.pages }}</td>
            <td>
              <template v-if="row.reporting">
                {{ compact(row.fans) }}
                <small v-if="row.fansDelta" :class="row.fansDelta > 0 ? 'up' : 'down'">{{ row.fansDelta > 0 ? '+' : '' }}{{ compact(row.fansDelta) }}</small>
              </template>
              <span v-else class="nw-na">—</span>
            </td>
            <td>{{ row.reporting ? compact(row.impressions) : '—' }}</td>
            <td>{{ row.reporting ? compact(row.engagement) : '—' }}</td>
            <td>{{ row.reporting && row.rate !== null ? `${row.rate.toFixed(1)}%` : '—' }}</td>
            <td>
              <span class="nw-posted">
                <span class="nw-bar"><span :style="{ width: `${row.postedShare}%` }"></span></span>
                {{ row.posted }}
              </span>
            </td>
          </tr>
          <!-- Détail par page (pages avec données uniquement) -->
          <template v-if="openKey === row.key">
            <tr v-for="page in row.details" :key="`${row.key}-${page.id}`" class="nw-detail">
              <td class="nw-detail-name">
                <span class="nw-detail-page">
                  <img v-if="page.avatar" :src="page.avatar" class="nw-avatar" alt="" loading="lazy" referrerpolicy="no-referrer" @error="$event.target.style.visibility = 'hidden'" />
                  <span v-else class="nw-avatar nw-avatar--empty">{{ (page.name || '?').charAt(0) }}</span>
                  <Tooltip :text="page.name" position="top" class="nw-detail-label"><span>{{ page.name }}</span></Tooltip>
                  <span v-if="byGroup" class="nw-net-tag" :class="`nw-net-tag--${page.network}`">{{ $t(`dash.overview.network.${page.network}`) }}</span>
                </span>
              </td>
              <td></td>
              <td>
                {{ page.page_fans !== null && page.page_fans !== undefined ? compact(page.page_fans) : '—' }}
                <small v-if="page.fans_delta" :class="page.fans_delta > 0 ? 'up' : 'down'">{{ page.fans_delta > 0 ? '+' : '' }}{{ compact(page.fans_delta) }}</small>
              </td>
              <td>{{ compact(page.impressions) }}</td>
              <td>{{ compact(page.engagement) }}</td>
              <td>{{ page.rate !== null ? `${page.rate.toFixed(1)}%` : '—' }}</td>
              <td>
                <span class="nw-posted">
                  <span class="nw-bar nw-bar--light"><span :style="{ width: `${page.postedShare}%` }"></span></span>
                  {{ page.posted || 0 }}
                </span>
              </td>
            </tr>
          </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Tooltip from '../tooltip/tooltipInit.vue'

const MODE_KEY = 'overview/performanceBy'
const readMode = () => {
  try { return localStorage.getItem(MODE_KEY) === 'group' ? 'group' : 'network' } catch (e) { return 'network' }
}

export default {
  name: 'OverviewNetworks',

  components: { Tooltip },

  props: {
    perPage: { type: Array, default: null },
    // Pages du compte (nom des pages dans le détail par réseau)
    pages: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      // Regroupement : 'network' (par réseau) ou 'group' (groupes de pages du client, comme SelectPage), mémorisé par navigateur
      mode: readMode(),
      openKey: null
    }
  },

  computed: {
    columns() {
      return [
        { key: 'pages', label: this.$t('dash.overview.networks.pages'), help: this.$t(this.byGroup ? 'dash.overview.help.groups_pages' : 'dash.overview.help.networks_pages') },
        { key: 'fans', label: this.$t('dash.reporting.followers'), help: this.$t('dash.overview.help.page.followers') },
        { key: 'impressions', label: this.$t('dash.reporting.impression'), help: this.$t('dash.overview.help.impressions') },
        { key: 'engagement', label: this.$t('dash.overview.kpi.engagement'), help: this.$t('dash.overview.help.engagement') },
        { key: 'rate', label: this.$t('dash.overview.kpi.engagement_rate'), help: this.$t('dash.overview.help.rate') },
        { key: 'posted', label: this.$t('dash.overview.networks.posted'), help: this.$t('dash.overview.help.by_network') }
      ]
    },

    byGroup() {
      return this.mode === 'group'
    },

    // Pages éligibles au Reporting ou ayant des données
    eligible() {
      return (this.perPage || []).filter(p => p.reporting || p.has_data)
    },

    // Ensembles de pages à agréger : un par réseau, ou un par groupe du client + « Autres pages » (pages hors groupe)
    sets() {
      if (!this.byGroup) {
        const byNetwork = {}
        this.eligible.forEach(p => (byNetwork[p.network] || (byNetwork[p.network] = [])).push(p))
        return Object.entries(byNetwork).map(([network, list]) => ({ key: network, label: this.$t(`dash.overview.network.${network}`), list }))
      }
      const byId = this.eligible.reduce((map, p) => ({ ...map, [String(p.id)]: p }), {})
      const grouped = new Set()
      const groups = Object.values(this.$store.getters['selectPage/selectPageGroups'] || {})
        .map(group => {
          const list = (group.pages || []).map(id => byId[String(id)]).filter(Boolean)
          list.forEach(p => grouped.add(String(p.id)))
          return { key: `group-${group.id}`, label: group.name, list }
        })
        .filter(g => g.list.length)
      const others = this.eligible.filter(p => !grouped.has(String(p.id)))
      if (others.length) {
        groups.push({ key: 'others', label: this.$t(groups.length ? 'dash.overview.groups.others' : 'dash.pages.group.all_pages'), list: others, last: true })
      }
      return groups
    },

    // Agrégat par ensemble, trié par publications puis interactions (« Autres pages » toujours en dernier)
    rows() {
      const rows = this.sets.map(set => {
        const row = { key: set.key, label: set.label, last: !!set.last, pages: set.list.length, reporting: false, fans: 0, fansDelta: 0, impressions: 0, engagement: 0, posted: 0 }
        set.list.forEach(p => {
          row.posted += p.posted || 0
          if (p.reporting && p.has_data) {
            row.reporting = true
            row.fans += p.page_fans || 0
            row.fansDelta += p.fans_delta || 0
            row.impressions += p.impressions || 0
            row.engagement += p.engagement || 0
          }
        })
        row.details = this.detailsOf(set.list)
        return row
      })
      const maxPosted = Math.max(1, ...rows.map(r => r.posted))
      return rows
        .map(r => ({ ...r, rate: r.impressions > 0 ? r.engagement / r.impressions * 100 : null, postedShare: Math.round(r.posted / maxPosted * 100) }))
        .sort((a, b) => a.last - b.last || b.posted - a.posted || b.engagement - a.engagement)
    }
  },

  methods: {
    // Pages de l'ensemble ayant des données sur la période, triées par interactions
    detailsOf(pages) {
      const list = pages.filter(p => p.has_data)
      const maxPosted = Math.max(1, ...list.map(p => p.posted || 0))
      return list
        .map(p => {
          const page = this.pages.find(pg => String(pg.id) === String(p.id))
          return {
            ...p,
            name: (page && page.name) || p.id,
            // Même source d'image que PageImageAvatar, en miniature pour garder des lignes fines
            avatar: (page && (page.avatar || page.picture || page.picture_url)) || null,
            rate: p.impressions > 0 ? p.engagement / p.impressions * 100 : null,
            postedShare: Math.round((p.posted || 0) / maxPosted * 100)
          }
        })
        .sort((a, b) => (b.engagement || 0) - (a.engagement || 0))
    },

    setMode(mode) {
      this.mode = mode
      this.openKey = null
      try { localStorage.setItem(MODE_KEY, mode) } catch (e) { /* indisponible */ }
    },

    toggle(row) {
      if (!row.details.length) return
      this.openKey = this.openKey === row.key ? null : row.key
    },

    compact(v) {
      const n = Number(v) || 0
      const abs = Math.abs(n)
      if (abs >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (abs >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return n.toLocaleString()
    }
  }
}
</script>

<style scoped>
.nw-table-wrap {
  overflow-x: auto;
}
.nw-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.8px;
  color: #121826;
}
.nw-table th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555e75;
  border-bottom: 1px solid #f1edf5;
  white-space: nowrap;
}
.nw-table td {
  padding: 10px;
  border-bottom: 1px solid #f8f5fa;
  white-space: nowrap;
}
.nw-table tbody tr:hover {
  background: #f6f1fa;
}
.nw-name {
  font-weight: 600;
}
.nw-row--expandable {
  cursor: pointer;
}
.nw-caret {
  font-size: 18px;
  color: #6b1f4d;
  vertical-align: middle;
  margin: -2px 2px 0 -4px;
}
.nw-row--open {
  background: #f6f1fa;
}
.nw-detail td {
  font-size: 12px;
  color: #555e75;
  background: #fbf9fc;
  border-bottom-color: #f1edf5;
}
.nw-detail-name {
  max-width: 260px;
  padding-left: 32px !important;
}
.nw-detail-page {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.nw-avatar {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  background: #f1edf5;
}
.nw-avatar--empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #6b1f4d;
  text-transform: uppercase;
}
.nw-detail-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nw-switch {
  margin-left: auto;
}
.nw-net-tag {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  background: #f1edf5;
  color: #6b1f4d;
}
.nw-net-tag--facebook { background: #e7f0fd; color: #1877f2; }
.nw-net-tag--instagram { background: #fde8f1; color: #c13584; }
.nw-net-tag--linkedin { background: #e6f1f8; color: #0a66c2; }
.nw-net-tag--tiktok,
.nw-net-tag--twitter { background: #eef0f3; color: #121826; }
.nw-net-tag--youtube { background: #fdeaea; color: #e62117; }
.nw-net-tag--google { background: #e8f0fe; color: #1a73e8; }
.nw-net-tag--pinterest { background: #fdeaea; color: #e60023; }
.nw-bar--light span {
  background: #ab6e93;
}
.nw-table small {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 700;
}
.nw-table small.up { color: #1a9e5e; }
.nw-table small.down { color: #d93025; }
.nw-na {
  color: #9ba3b7;
}
.nw-posted {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.nw-bar {
  width: 80px;
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.nw-bar span {
  display: block;
  height: 100%;
  background: #6b1f4d;
  border-radius: 3px;
}
.nw-skeleton {
  height: 160px;
  border-radius: 5.6px;
  background: #f1edf5;
}
</style>
